import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { api, useAuthStore } from '@/store';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Store, Plus, Trash2, Link2, Copy, DollarSign, Users, X, Edit2, TrendingUp, CheckCircle2, Clock, Loader2, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';

export default function CreditStorePage() {
  const { user } = useAuthStore();
  const [integrations, setIntegrations] = useState([]);
  const [resellers, setResellers] = useState([]);
  const [vendas, setVendas] = useState(null);
  const [buscaRev, setBuscaRev] = useState(''); const [pageRev, setPageRev] = useState(1);
  const [buscaVenda, setBuscaVenda] = useState(''); const [pageVenda, setPageVenda] = useState(1);
  const PER_PAGE = 15;
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('resellers');
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [pricingInteg, setPricingInteg] = useState('');
  const [tiers, setTiers] = useState([]);
  const [pacotes, setPacotes] = useState([]);
  const [pmodo, setPmodo] = useState('quantidade');
  const [maxCompra, setMaxCompra] = useState('');
  const [pricingModal, setPricingModal] = useState(null);
  const [tierModal, setTierModal] = useState(null); // { idx, min_qtd, max_qtd, preco_unitario } — edição focada de uma faixa
  const [pacoteModal, setPacoteModal] = useState(null);
  const [customPrecos, setCustomPrecos] = useState(null); // { reseller_id, integration_id, nome_integ }
  const [cfg, setCfg] = useState({ signup_enabled: false, public_integrations: [] });

  const lojaLink = `${window.location.origin}/loja/${user?.id}`;
  const brl = (v) => `R$ ${Number(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`;

  const load = async () => {
    setLoading(true);
    try {
      const [ri, rr, rv, rc] = await Promise.all([
        api.get('/credit-store/integrations'),
        api.get('/credit-store/resellers'),
        api.get('/credit-store/vendas'),
        api.get('/credit-store/config'),
      ]);
      setIntegrations(ri.data.integrations || []);
      setResellers(rr.data.resellers || []);
      setVendas(rv.data);
      setCfg({ signup_enabled: !!rc.data.signup_enabled, public_integrations: rc.data.public_integrations || [] });
      if (!pricingInteg && ri.data.integrations?.length) setPricingInteg(ri.data.integrations[0].id);
    } catch (e) { toast.error('Erro ao carregar'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);
  // atualização automática (só quando nenhum modal está aberto, para não piscar)
  useEffect(() => {
    if (editing || pricingModal) return;
    const iv = setInterval(() => {
      api.get('/credit-store/vendas').then(r => setVendas(r.data)).catch(() => {});
      api.get('/credit-store/resellers').then(r => setResellers(r.data.resellers || [])).catch(() => {});
    }, 10000);
    return () => clearInterval(iv);
  }, [editing, pricingModal]);

  const loadPricing = async (iid) => {
    if (!iid) return;
    try { const r = await api.get(`/credit-store/pricing/${iid}`); setTiers(r.data.tiers || []); setMaxCompra(r.data.max_por_compra || ''); } catch {}
  };
  useEffect(() => { if (pricingInteg) loadPricing(pricingInteg); }, [pricingInteg]);

  const novoRevendedor = () => setEditing({ novo: true, nome: '', username: '', password: '', integracoes: [], active: true });
  const editarRevendedor = async (r) => {
    try { const full = await api.get(`/credit-store/resellers/${r.id}`); setEditing({ ...full.data, novo: false, password: '' }); }
    catch { toast.error('Erro ao abrir'); }
  };
  const toggleIntegNoEditor = (ig) => {
    const arr = editing.integracoes || [];
    const has = arr.find(x => x.integration_id === ig.id);
    if (has) setEditing({ ...editing, integracoes: arr.filter(x => x.integration_id !== ig.id) });
    else setEditing({ ...editing, integracoes: [...arr, { integration_id: ig.id, panel_username: '' }] });
  };
  const setPanelUser = (iid, val) => setEditing({ ...editing, integracoes: editing.integracoes.map(x => x.integration_id === iid ? { ...x, panel_username: val } : x) });

  const abrirCustomPrecos = async (ig) => {
    const iid = ig.integration_id;
    const nome = integrations.find(i => i.id === iid)?.name || 'Integração';
    const prov = integrations.find(i => i.id === iid)?.provider;
    setCustomPrecos({ reseller_id: editing.id, integration_id: iid, nome, provider: prov });
    // carrega personalizado atual (se houver) do editing
    const pp = (editing.precos_personalizados || {})[iid];
    if (pp) { setPmodo(pp.modo || 'quantidade'); setTiers(pp.tiers || []); setPacotes(pp.pacotes || []); setMaxCompra(pp.max_por_compra || ''); }
    else { setPmodo('quantidade'); setTiers([]); setPacotes([]); setMaxCompra(''); }
  };
  const salvarCustomPrecos = async () => {
    const clean = tiers.filter(t => t.min_qtd && t.preco_unitario).map(t => ({ min_qtd: parseInt(t.min_qtd), max_qtd: parseInt(t.max_qtd) || 0, preco_unitario: parseFloat(t.preco_unitario) }));
    const cleanPac = pacotes.filter(p => p.creditos && p.preco).map(p => ({ creditos: parseInt(p.creditos), preco: parseFloat(p.preco), nome: p.nome || '' }));
    try {
      await api.put(`/credit-store/resellers/${customPrecos.reseller_id}/precos`, { integration_id: customPrecos.integration_id, modo: pmodo, tiers: clean, pacotes: cleanPac, max_por_compra: maxCompra ? parseInt(maxCompra) : null });
      toast.success('Preços personalizados salvos!');
      setCustomPrecos(null);
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro'); }
  };
  const salvarRevendedor = async () => {
    const e = editing;
    if (!e.nome?.trim()) return toast.error('Informe o nome');
    if (e.novo && (!e.username?.trim() || !e.password?.trim())) return toast.error('Usuário e senha da loja obrigatórios');
    if (!e.integracoes?.length) return toast.error('Libere ao menos uma integração');
    for (const ig of e.integracoes) if (!ig.panel_username?.trim()) return toast.error('Preencha o usuário do revendedor em cada painel liberado');
    setSaving(true);
    try {
      if (e.novo) {
        await api.post('/credit-store/resellers', { nome: e.nome, username: e.username, password: e.password, integracoes: e.integracoes });
        toast.success('Revendedor criado e validado nos painéis!');
      } else {
        const body = { nome: e.nome, active: e.active, integracoes: e.integracoes };
        if (e.password) body.password = e.password;
        await api.put(`/credit-store/resellers/${e.id}`, body);
        toast.success('Revendedor atualizado!');
      }
      setEditing(null); load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erro ao salvar'); }
    finally { setSaving(false); }
  };
  const delRevendedor = async (id) => { if (!window.confirm('Excluir este revendedor?')) return; try { await api.delete(`/credit-store/resellers/${id}`); load(); } catch {} };

  const abrirPrecos = async (ig) => {
    setPricingModal(ig); setPricingInteg(ig.id);
    const min0 = ig.provider === 'geradorpro' ? 10 : 1;
    try {
      const r = await api.get(`/credit-store/pricing/${ig.id}`);
      const t = r.data.tiers || [];
      setTiers(t.length ? t : [{ min_qtd: String(min0), max_qtd: '', preco_unitario: '' }]);
      setPacotes(r.data.pacotes || []);
      setPmodo(r.data.modo || 'quantidade');
      setMaxCompra(r.data.max_por_compra || '');
    } catch { setTiers([{ min_qtd: String(min0), max_qtd: '', preco_unitario: '' }]); setPacotes([]); setPmodo('quantidade'); setMaxCompra(''); }
  };
  const minCreditos = ((pricingModal?.provider || customPrecos?.provider) === 'geradorpro') ? 10 : 1;
  const salvarCfg = async (novoCfg) => {
    const c = novoCfg || cfg;
    try { await api.put('/credit-store/config', c); setCfg(c); toast.success('Configuração salva!'); }
    catch (e) { toast.error(e.response?.data?.detail || 'Erro'); }
  };
  const togglePublicInteg = (iid) => {
    const has = cfg.public_integrations.includes(iid);
    const novo = { ...cfg, public_integrations: has ? cfg.public_integrations.filter(x => x !== iid) : [...cfg.public_integrations, iid] };
    setCfg(novo);
  };
  const addTier = () => {
    // sugere o mínimo automático: 1ª faixa começa no mínimo do provider; próximas seguem a anterior
    const ult = tiers[tiers.length - 1];
    const proxMin = ult && ult.max_qtd ? (parseInt(ult.max_qtd) + 1) : minCreditos;
    setTiers([...tiers, { min_qtd: String(proxMin), max_qtd: '', preco_unitario: '' }]);
  };
  const addPacote = () => setPacotes([...pacotes, { creditos: pricingModal?.provider === 'geradorpro' ? '10' : '', preco: '', nome: '' }]);
  const salvarPricing = async () => {
    const clean = tiers.filter(t => t.min_qtd && t.preco_unitario).map(t => ({ min_qtd: parseInt(t.min_qtd), max_qtd: parseInt(t.max_qtd) || 0, preco_unitario: parseFloat(t.preco_unitario) }));
    const cleanPac = pacotes.filter(p => p.creditos && p.preco).map(p => ({ creditos: parseInt(p.creditos), preco: parseFloat(p.preco), nome: p.nome || '' }));
    if (pricingModal?.provider === 'geradorpro') {
      if (pmodo === 'quantidade' && clean.some(t => t.min_qtd < 10)) { toast.error('No Gerador Pro o mínimo é 10 créditos. Ajuste a faixa inicial.'); return; }
      if (pmodo === 'pacotes' && cleanPac.some(p => p.creditos < 10)) { toast.error('No Gerador Pro os pacotes precisam ter no mínimo 10 créditos.'); return; }
    }
    if (pmodo === 'quantidade' && clean.length === 0) { toast.error('Adicione ao menos uma faixa de preço'); return; }
    if (pmodo === 'pacotes' && cleanPac.length === 0) { toast.error('Adicione ao menos um pacote'); return; }
    try { await api.put('/credit-store/pricing', { integration_id: pricingInteg, modo: pmodo, tiers: clean, pacotes: cleanPac, max_por_compra: maxCompra ? parseInt(maxCompra) : null }); toast.success('Preços salvos!'); }
    catch (e) { toast.error(e.response?.data?.detail || 'Erro'); }
  };

  const Pager = ({ page, setPage, total }) => {
    const pages = Math.ceil(total / PER_PAGE);
    if (pages <= 1) return null;
    return (
      <div className="flex items-center justify-center gap-2 pt-2">
        <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(page - 1)}>Anterior</Button>
        <span className="text-xs text-muted-foreground">{page} / {pages}</span>
        <Button size="sm" variant="outline" disabled={page >= pages} onClick={() => setPage(page + 1)}>Próxima</Button>
      </div>
    );
  };
  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;

  return (
    <div className="space-y-6 animate-fade-in max-w-4xl mx-auto pb-10">
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-cyan-500/25 to-purple-500/25 flex items-center justify-center"><Store className="w-6 h-6 text-cyan-400" /></div>
        <div>
          <h1 className="font-heading font-bold text-2xl text-foreground">Loja de Créditos</h1>
          <p className="text-sm text-muted-foreground">Venda créditos das suas integrações aos revendedores, automaticamente.</p>
        </div>
      </div>

      {vendas && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card className="glass-card"><CardContent className="p-4"><p className="text-[11px] text-muted-foreground uppercase tracking-wide">Receita total</p><p className="text-xl font-bold text-green-400">{brl(vendas.total_receita)}</p></CardContent></Card>
          <Card className="glass-card"><CardContent className="p-4"><p className="text-[11px] text-muted-foreground uppercase tracking-wide">No mês</p><p className="text-xl font-bold text-cyan-400">{brl(vendas.receita_mes)}</p></CardContent></Card>
          <Card className="glass-card"><CardContent className="p-4"><p className="text-[11px] text-muted-foreground uppercase tracking-wide">Créditos vendidos</p><p className="text-xl font-bold text-foreground">{vendas.total_creditos}</p></CardContent></Card>
          <Card className="glass-card"><CardContent className="p-4"><p className="text-[11px] text-muted-foreground uppercase tracking-wide">Vendas</p><p className="text-xl font-bold text-foreground">{vendas.total_vendas}</p></CardContent></Card>
        </div>
      )}

      <Card className="glass-card"><CardContent className="p-4 flex items-center gap-3">
        <Link2 className="w-4 h-4 text-primary shrink-0" />
        <div className="flex-1 min-w-0"><p className="text-xs text-muted-foreground">Link da sua loja (envie aos revendedores)</p><p className="text-sm font-mono text-foreground truncate">{lojaLink}</p></div>
        <Button size="sm" variant="outline" onClick={() => { navigator.clipboard.writeText(lojaLink); toast.success('Link copiado!'); }}><Copy className="w-4 h-4 mr-1" /> Copiar</Button>
      </CardContent></Card>

      <div className="flex gap-1 bg-muted/50 rounded-lg p-1 w-fit">
        {[{ k: 'resellers', label: 'Revendedores', icon: Users }, { k: 'pricing', label: 'Preços', icon: DollarSign }, { k: 'vendas', label: 'Vendas', icon: TrendingUp }, { k: 'config', label: 'Configurações', icon: ShieldCheck }].map(t => (
          <button key={t.k} onClick={() => setTab(t.k)} className={`px-4 py-1.5 rounded-md text-sm flex items-center gap-1.5 transition-all ${tab === t.k ? 'bg-background shadow text-foreground' : 'text-muted-foreground hover:text-foreground'}`}><t.icon className="w-4 h-4" /> {t.label}</button>
        ))}
      </div>

      {tab === 'resellers' && (() => {
        const filtrados = resellers.filter(r => !buscaRev.trim() || (r.username || '').toLowerCase().includes(buscaRev.toLowerCase().trim()) || (r.nome || '').toLowerCase().includes(buscaRev.toLowerCase().trim()));
        const pag = filtrados.slice((pageRev - 1) * PER_PAGE, pageRev * PER_PAGE);
        return (
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <div className="relative flex-1">
              <Input value={buscaRev} onChange={e => { setBuscaRev(e.target.value); setPageRev(1); }} placeholder="Pesquisar por usuário ou nome..." className="pl-3" />
            </div>
            <Button size="sm" onClick={novoRevendedor}><Plus className="w-4 h-4 mr-1" /> Novo</Button>
          </div>
          {filtrados.length === 0 && <Card className="glass-card"><CardContent className="p-8 text-center"><Users className="w-10 h-10 text-muted-foreground/40 mx-auto mb-2" /><p className="text-sm text-muted-foreground">{buscaRev ? 'Nenhum revendedor encontrado.' : 'Nenhum revendedor ainda.'}</p></CardContent></Card>}
          {pag.map(r => (
            <Card key={r.id} className="glass-card hover-lift"><CardContent className="p-4 flex items-center justify-between gap-3">
              <div className="min-w-0 flex items-center gap-3">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold ${r.active ? 'bg-cyan-500/15 text-cyan-400' : 'bg-muted text-muted-foreground'}`}>{(r.nome || '?')[0].toUpperCase()}</div>
                <div className="min-w-0">
                  <p className="font-medium text-foreground truncate">{r.nome} <span className="text-xs text-muted-foreground font-mono">@{r.username}</span></p>
                  <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                    {(r.integracoes || []).map((it, i) => { const ig = integrations.find(x => x.id === (it.integration_id || it)); return <span key={i} className="text-[10px] px-1.5 py-0.5 rounded bg-white/[0.06] text-white/60">{ig?.name || 'painel'}</span>; })}
                    {r.two_factor_enabled && <span className="text-[10px] text-green-400 flex items-center gap-0.5"><ShieldCheck className="w-3 h-3" /> 2FA</span>}
                    {!r.active && <span className="text-[10px] text-red-400">inativo</span>}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => editarRevendedor(r)}><Edit2 className="w-4 h-4" /></Button>
                <Button size="icon" variant="ghost" className="text-red-500 h-8 w-8" onClick={() => delRevendedor(r.id)}><Trash2 className="w-4 h-4" /></Button>
              </div>
            </CardContent></Card>
          ))}
          <Pager page={pageRev} setPage={setPageRev} total={filtrados.length} />
        </div>
        );
      })()}

      {tab === 'pricing' && (
        <div className="space-y-3">
          <p className="text-sm text-muted-foreground">Clique numa integração para configurar os preços de crédito dela.</p>
          {integrations.map(ig => (
            <Card key={ig.id} className="glass-card hover-lift cursor-pointer" onClick={() => abrirPrecos(ig)}>
              <CardContent className="p-4 flex items-center justify-between gap-3">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-500/25 to-purple-500/25 flex items-center justify-center shrink-0"><DollarSign className="w-5 h-5 text-cyan-400" /></div>
                  <div className="min-w-0">
                    <p className="font-medium text-foreground truncate">{ig.name || ig.panel_username}</p>
                    <p className="text-xs text-muted-foreground">{ig.provider} · toque para configurar preços</p>
                  </div>
                </div>
                <Edit2 className="w-4 h-4 text-muted-foreground shrink-0" />
              </CardContent>
            </Card>
          ))}
          {integrations.length === 0 && <Card className="glass-card"><CardContent className="p-8 text-center"><DollarSign className="w-10 h-10 text-muted-foreground/40 mx-auto mb-2" /><p className="text-sm text-muted-foreground">Sem integrações Sigma/PainelFoda.</p></CardContent></Card>}
        </div>
      )}

      {tab === 'vendas' && (() => {
        const todas = vendas?.vendas || [];
        const filtradas = todas.filter(v => !buscaVenda.trim() || (v.revendedor || '').toLowerCase().includes(buscaVenda.toLowerCase().trim()) || (v.integracao || '').toLowerCase().includes(buscaVenda.toLowerCase().trim()));
        const pag = filtradas.slice((pageVenda - 1) * PER_PAGE, pageVenda * PER_PAGE);
        return (
        <div className="space-y-2">
          <Input value={buscaVenda} onChange={e => { setBuscaVenda(e.target.value); setPageVenda(1); }} placeholder="Pesquisar por revendedor ou integração..." />
          {filtradas.length === 0 && <Card className="glass-card"><CardContent className="p-8 text-center"><TrendingUp className="w-10 h-10 text-muted-foreground/40 mx-auto mb-2" /><p className="text-sm text-muted-foreground">{buscaVenda ? 'Nenhuma venda encontrada.' : 'Nenhuma venda ainda.'}</p></CardContent></Card>}
          {pag.map(v => (
            <Card key={v.id} className="glass-card"><CardContent className="p-3 flex items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="text-sm font-medium text-foreground truncate">{v.revendedor} <span className="text-xs text-muted-foreground">· {v.integracao}</span></p>
                <p className="text-[11px] text-muted-foreground">{new Date(v.data).toLocaleString('pt-BR')} · {v.quantidade} créditos</p>
              </div>
              <div className="text-right shrink-0">
                <p className="text-sm font-bold text-green-400">{brl(v.valor)}</p>
                <div className="flex items-center gap-1 justify-end text-[10px]">
                  {v.status === 'approved' ? <span className="text-green-400 flex items-center gap-0.5"><CheckCircle2 className="w-3 h-3" /> pago</span> : <span className="text-amber-400 flex items-center gap-0.5"><Clock className="w-3 h-3" /> pendente</span>}
                  {v.status === 'approved' && v.deposito === 'feito' && <span className="text-cyan-400">· depositado</span>}
                  {v.status === 'approved' && v.deposito !== 'feito' && <span className="text-amber-400">· dep. pendente</span>}
                </div>
              </div>
            </CardContent></Card>
          ))}
          <Pager page={pageVenda} setPage={setPageVenda} total={filtradas.length} />
        </div>
        );
      })()}


      {tab === 'config' && (
        <div className="space-y-4">
          <Card className="glass-card">
            <CardContent className="p-5 space-y-4">
              <label className="flex items-center justify-between cursor-pointer">
                <div>
                  <p className="text-sm font-semibold text-foreground">Permitir auto-cadastro</p>
                  <p className="text-xs text-muted-foreground mt-0.5">Deixa qualquer pessoa criar uma conta na sua loja e se vincular às integrações que você liberar.</p>
                </div>
                <Switch checked={cfg.signup_enabled} onCheckedChange={(v) => salvarCfg({ ...cfg, signup_enabled: v })} />
              </label>

              {cfg.signup_enabled && (
                <div className="pt-3 border-t border-border/50">
                  <p className="text-sm font-medium text-foreground mb-1">Integrações disponíveis no cadastro</p>
                  <p className="text-xs text-muted-foreground mb-3">Quem se cadastrar poderá se auto-vincular a estas (informando o usuário dele, que será validado no painel).</p>
                  {integrations.some(ig => ig.provider === 'geradorpro' && cfg.public_integrations.includes(ig.id)) && (
                    <div className="mb-3 rounded-lg bg-amber-500/10 border border-amber-500/30 p-2.5">
                      <p className="text-[11px] text-amber-600 dark:text-amber-400">⚠️ <b>Gerador Pro:</b> só usuários <b>MASTER</b> ou <b>ULTRA MASTER</b> recebem créditos. Clientes comuns não conseguem receber, mesmo pagando — eles serão avisados disso na loja.</p>
                    </div>
                  )}
                  <div className="space-y-2">
                    {integrations.map(ig => (
                      <label key={ig.id} className="flex items-center gap-3 p-3 rounded-lg bg-muted/40 cursor-pointer">
                        <Switch checked={cfg.public_integrations.includes(ig.id)} onCheckedChange={() => togglePublicInteg(ig.id)} />
                        <span className="text-sm text-foreground flex-1">{ig.name || ig.panel_username}</span>
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-white/[0.06] text-white/50">{ig.provider}</span>
                      </label>
                    ))}
                    {integrations.length === 0 && <p className="text-xs text-muted-foreground">Sem integrações Sigma/PainelFoda/Gerador Pro/FShorts.</p>}
                  </div>
                  <Button className="w-full mt-3" onClick={() => salvarCfg()}>Salvar integrações liberadas</Button>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* MODAL DE PREÇOS (geral OU personalizado do revendedor) */}
      {(pricingModal || customPrecos) && createPortal((
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 p-4" onClick={() => { setPricingModal(null); setCustomPrecos(null); }}>
          <div className="w-full max-w-lg rounded-2xl bg-card border border-border shadow-2xl flex flex-col" style={{ maxHeight: '85vh' }} onClick={e => e.stopPropagation()}>
            <div className="shrink-0 border-b border-border p-4 flex items-center justify-between">
              <h3 className="font-semibold text-foreground flex items-center gap-2"><DollarSign className="w-5 h-5 text-primary" /> {customPrecos ? `Preços de ${editing?.nome || 'cliente'} — ${customPrecos.nome}` : `Preços — ${pricingModal.name}`}</h3>
              <button onClick={() => { setPricingModal(null); setCustomPrecos(null); }} className="text-muted-foreground hover:text-foreground"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-5 space-y-4 overflow-y-auto">
              {(pricingModal?.provider === 'geradorpro' || customPrecos?.provider === 'geradorpro') && <div className="text-[11px] text-amber-600 dark:text-amber-400 bg-amber-500/10 border border-amber-500/20 rounded-lg px-3 py-2">⚠️ Gerador Pro: mínimo <b>10 créditos</b>. E o cliente precisa ser <b>MASTER ou ULTRA MASTER</b> pra receber créditos.</div>}

              {/* Seletor de modo */}
              <div>
                <Label className="text-xs text-muted-foreground mb-2 block">Modo de venda</Label>
                <div className="grid grid-cols-2 gap-2">
                  <button onClick={() => setPmodo('quantidade')} className={`rounded-xl border p-3 text-left transition-all ${pmodo === 'quantidade' ? 'border-primary bg-primary/5 ring-1 ring-primary/30' : 'border-border hover:border-border/80'}`}>
                    <p className="text-sm font-semibold text-foreground">Por quantidade</p>
                    <p className="text-[11px] text-muted-foreground mt-0.5">Cliente escolhe quanto quer, preço por faixa</p>
                  </button>
                  <button onClick={() => setPmodo('pacotes')} className={`rounded-xl border p-3 text-left transition-all ${pmodo === 'pacotes' ? 'border-primary bg-primary/5 ring-1 ring-primary/30' : 'border-border hover:border-border/80'}`}>
                    <p className="text-sm font-semibold text-foreground">Pacotes fechados</p>
                    <p className="text-[11px] text-muted-foreground mt-0.5">Você define pacotes prontos com preço fixo</p>
                  </button>
                </div>
              </div>

              {/* MODO QUANTIDADE — só visualização, clique abre modal focado */}
              {pmodo === 'quantidade' && (
                <div className="space-y-2">
                  <Label className="text-xs text-muted-foreground">Faixas de preço (clique para editar)</Label>
                  {tiers.length === 0 && <p className="text-xs text-muted-foreground text-center py-3">Nenhuma faixa. Adicione a primeira.</p>}
                  {tiers.map((t, i) => {
                    const semLimite = !t.max_qtd || parseInt(t.max_qtd) <= 0;
                    return (
                      <div key={i} className="flex items-center justify-between rounded-xl bg-muted/40 border border-border/50 px-4 py-2.5 cursor-pointer hover:border-primary/40 transition-colors"
                        onClick={() => setTierModal({ idx: i, ...t })}>
                        <span className="text-sm text-foreground">
                          {semLimite ? `A partir de ${t.min_qtd || 0} créditos` : `${t.min_qtd || 0} a ${t.max_qtd} créditos`}
                        </span>
                        <div className="flex items-center gap-3">
                          <span className="text-sm font-semibold text-foreground">R$ {Number(t.preco_unitario || 0).toFixed(2)}<span className="text-[11px] text-muted-foreground font-normal">/un</span></span>
                          <Button size="icon" variant="ghost" className="text-red-500 h-7 w-7" onClick={(e) => { e.stopPropagation(); setTiers(tiers.filter((_, x) => x !== i)); }}><X className="w-4 h-4" /></Button>
                        </div>
                      </div>
                    );
                  })}
                  <Button size="sm" variant="outline" className="w-full" onClick={() => {
                    const ult = tiers[tiers.length - 1];
                    const proxMin = ult && ult.max_qtd ? (parseInt(ult.max_qtd) + 1) : minCreditos;
                    setTierModal({ idx: -1, min_qtd: String(proxMin), max_qtd: '', preco_unitario: '' });
                  }}><Plus className="w-4 h-4 mr-1" /> Adicionar faixa</Button>
                </div>
              )}

              {/* MODO PACOTES — só visualização, clique abre modal focado */}
              {pmodo === 'pacotes' && (
                <div className="space-y-2">
                  <Label className="text-xs text-muted-foreground">Pacotes (clique para editar)</Label>
                  {pacotes.length === 0 && <p className="text-xs text-muted-foreground text-center py-3">Nenhum pacote. Adicione o primeiro.</p>}
                  {pacotes.map((p, i) => (
                    <div key={i} className="flex items-center justify-between rounded-xl bg-muted/40 border border-border/50 px-4 py-2.5 cursor-pointer hover:border-primary/40 transition-colors"
                      onClick={() => setPacoteModal({ idx: i, ...p })}>
                      <div>
                        <p className="text-sm font-medium text-foreground">{p.creditos || 0} créditos{p.nome ? ` · ${p.nome}` : ''}</p>
                        {p.creditos && p.preco ? <p className="text-[11px] text-muted-foreground">R$ {(parseFloat(p.preco) / parseInt(p.creditos)).toFixed(2)}/crédito</p> : null}
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-sm font-semibold text-foreground">R$ {Number(p.preco || 0).toFixed(2)}</span>
                        <Button size="icon" variant="ghost" className="text-red-500 h-7 w-7" onClick={(e) => { e.stopPropagation(); setPacotes(pacotes.filter((_, x) => x !== i)); }}><X className="w-4 h-4" /></Button>
                      </div>
                    </div>
                  ))}
                  <Button size="sm" variant="outline" className="w-full" onClick={() => setPacoteModal({ idx: -1, creditos: pricingModal?.provider === 'geradorpro' ? '10' : '', preco: '', nome: '' })}><Plus className="w-4 h-4 mr-1" /> Adicionar pacote</Button>
                </div>
              )}

              <div className="pt-3 border-t border-border/50">
                <Label className="text-xs text-muted-foreground">Máximo por compra (opcional)</Label>
                <Input type="number" value={maxCompra} onChange={e => setMaxCompra(e.target.value)} placeholder="sem limite" className="mt-1 w-40 h-9" />
              </div>
              <Button onClick={async () => { if (customPrecos) { await salvarCustomPrecos(); } else { await salvarPricing(); setPricingModal(null); } }} className="w-full">Salvar preços</Button>
            </div>
          </div>
        </div>
      ), document.body)}

      {/* MODAL FOCADO — FAIXA */}
      {tierModal && createPortal((
        <div className="fixed inset-0 z-[110] flex items-center justify-center bg-black/70 p-4" onClick={() => setTierModal(null)}>
          <div className="w-full max-w-sm rounded-2xl bg-card border border-border shadow-2xl p-5" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-foreground">{tierModal.idx === -1 ? 'Nova faixa' : 'Editar faixa'}</h3>
              <button onClick={() => setTierModal(null)} className="text-muted-foreground hover:text-foreground"><X className="w-5 h-5" /></button>
            </div>
            <div className="space-y-4">
              <div>
                <Label className="text-xs">A partir de (créditos)</Label>
                <Input type="number" value={tierModal.min_qtd} onChange={e => setTierModal({ ...tierModal, min_qtd: e.target.value })} className="mt-1 h-11 text-base" autoFocus />
              </div>
              <div>
                <div className="flex items-center justify-between">
                  <Label className="text-xs">Até (créditos)</Label>
                  <label className="text-[11px] text-muted-foreground flex items-center gap-1.5 cursor-pointer">
                    <Switch checked={!tierModal.max_qtd || parseInt(tierModal.max_qtd) <= 0} onCheckedChange={(v) => setTierModal({ ...tierModal, max_qtd: v ? '' : '999' })} />
                    sem limite (a partir de)
                  </label>
                </div>
                <Input type="number" value={tierModal.max_qtd} onChange={e => setTierModal({ ...tierModal, max_qtd: e.target.value })} placeholder="deixe vazio p/ sem limite" className="mt-1 h-11 text-base" disabled={!tierModal.max_qtd && tierModal.max_qtd !== '0' ? false : false} />
              </div>
              <div>
                <Label className="text-xs">Preço por crédito (R$)</Label>
                <Input type="number" step="0.01" value={tierModal.preco_unitario} onChange={e => setTierModal({ ...tierModal, preco_unitario: e.target.value })} placeholder="0,00" className="mt-1 h-11 text-base" />
              </div>
              <Button className="w-full" onClick={() => {
                if (!tierModal.min_qtd || !tierModal.preco_unitario) { toast.error('Preencha a quantidade inicial e o preço'); return; }
                const nova = { min_qtd: tierModal.min_qtd, max_qtd: tierModal.max_qtd || '', preco_unitario: tierModal.preco_unitario };
                if (tierModal.idx === -1) setTiers([...tiers, nova]);
                else setTiers(tiers.map((t, x) => x === tierModal.idx ? nova : t));
                setTierModal(null);
              }}>Salvar faixa</Button>
            </div>
          </div>
        </div>
      ), document.body)}

      {/* MODAL FOCADO — PACOTE */}
      {pacoteModal && createPortal((
        <div className="fixed inset-0 z-[110] flex items-center justify-center bg-black/70 p-4" onClick={() => setPacoteModal(null)}>
          <div className="w-full max-w-sm rounded-2xl bg-card border border-border shadow-2xl p-5" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-foreground">{pacoteModal.idx === -1 ? 'Novo pacote' : 'Editar pacote'}</h3>
              <button onClick={() => setPacoteModal(null)} className="text-muted-foreground hover:text-foreground"><X className="w-5 h-5" /></button>
            </div>
            <div className="space-y-4">
              <div>
                <Label className="text-xs">Nome (opcional)</Label>
                <Input value={pacoteModal.nome} onChange={e => setPacoteModal({ ...pacoteModal, nome: e.target.value })} placeholder="Ex.: Pacote Bronze" className="mt-1 h-11 text-base" />
              </div>
              <div>
                <Label className="text-xs">Quantidade de créditos</Label>
                <Input type="number" value={pacoteModal.creditos} onChange={e => setPacoteModal({ ...pacoteModal, creditos: e.target.value })} className="mt-1 h-11 text-base" autoFocus />
              </div>
              <div>
                <Label className="text-xs">Preço do pacote (R$)</Label>
                <Input type="number" step="0.01" value={pacoteModal.preco} onChange={e => setPacoteModal({ ...pacoteModal, preco: e.target.value })} placeholder="0,00" className="mt-1 h-11 text-base" />
              </div>
              {pacoteModal.creditos && pacoteModal.preco ? <p className="text-xs text-muted-foreground text-center">= R$ {(parseFloat(pacoteModal.preco) / parseInt(pacoteModal.creditos)).toFixed(2)} por crédito</p> : null}
              <Button className="w-full" onClick={() => {
                if (!pacoteModal.creditos || !pacoteModal.preco) { toast.error('Preencha os créditos e o preço'); return; }
                const nova = { creditos: pacoteModal.creditos, preco: pacoteModal.preco, nome: pacoteModal.nome || '' };
                if (pacoteModal.idx === -1) setPacotes([...pacotes, nova]);
                else setPacotes(pacotes.map((p, x) => x === pacoteModal.idx ? nova : p));
                setPacoteModal(null);
              }}>Salvar pacote</Button>
            </div>
          </div>
        </div>
      ), document.body)}

      {editing && createPortal((
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 p-4" onClick={() => setEditing(null)}>
          <div className="w-full max-w-lg rounded-2xl bg-card border border-border shadow-2xl flex flex-col" style={{ maxHeight: '88vh' }} onClick={e => e.stopPropagation()}>
            <div className="shrink-0 border-b border-border p-4 flex items-center justify-between">
              <h3 className="font-semibold text-foreground flex items-center gap-2"><Users className="w-5 h-5 text-primary" /> {editing.novo ? 'Novo revendedor' : 'Editar revendedor'}</h3>
              <button onClick={() => setEditing(null)} className="text-muted-foreground hover:text-foreground"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-4 space-y-4 overflow-y-auto">
              <div><Label className="text-xs">Nome</Label><Input value={editing.nome} onChange={e => setEditing({ ...editing, nome: e.target.value })} placeholder="Nome do revendedor" className="mt-1" /></div>
              <div className="rounded-xl border border-cyan-500/20 bg-cyan-500/[0.04] p-3">
                <p className="text-xs font-semibold text-cyan-400 mb-2">🔑 Acesso à loja (login dele)</p>
                <div className="grid grid-cols-2 gap-2">
                  <div><Label className="text-[11px]">Usuário</Label><Input value={editing.username || ''} disabled={!editing.novo} onChange={e => setEditing({ ...editing, username: e.target.value })} placeholder="usuario" className="mt-1 h-9" /></div>
                  <div><Label className="text-[11px]">{editing.novo ? 'Senha' : 'Nova senha (opcional)'}</Label><Input type="password" value={editing.password || ''} onChange={e => setEditing({ ...editing, password: e.target.value })} placeholder={editing.novo ? 'senha' : 'em branco = manter'} className="mt-1 h-9" /></div>
                </div>
                {!editing.novo && <p className="text-[10px] text-muted-foreground mt-1">O usuário da loja não muda.</p>}
              </div>
              <div>
                <Label className="text-xs">Integrações liberadas + usuário dele em cada painel</Label>
                <p className="text-[11px] text-muted-foreground mb-2">Ao salvar, validamos se o usuário existe no painel.</p>
                <div className="space-y-2">
                  {integrations.map(ig => {
                    const sel = editing.integracoes?.find(x => x.integration_id === ig.id);
                    return (
                      <div key={ig.id} className={`rounded-lg border p-3 transition-all ${sel ? 'border-cyan-500/30 bg-cyan-500/[0.04]' : 'border-border bg-muted/30'}`}>
                        <div className="flex items-center gap-2">
                          <Switch checked={!!sel} onCheckedChange={() => toggleIntegNoEditor(ig)} />
                          <span className="text-sm text-foreground flex-1">{ig.name || ig.panel_username}</span>
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-white/[0.06] text-white/50">{ig.provider}</span>
                        </div>
                        {sel && <div className="mt-2 pl-8"><Label className="text-[11px]">Usuário do revendedor no painel {ig.name}</Label><Input value={sel.panel_username || ''} onChange={e => setPanelUser(ig.id, e.target.value)} placeholder="usuário dele no painel" className="mt-1 h-9" /></div>}
                      </div>
                    );
                  })}
                  {integrations.length === 0 && <p className="text-xs text-muted-foreground">Você não tem integrações Sigma ou PainelFoda.</p>}
                </div>
              </div>
              {!editing.novo && (editing.integracoes || []).length > 0 && (
                <div className="rounded-lg border border-border bg-muted/20 p-3">
                  <p className="text-xs font-medium text-foreground mb-1">💲 Preços personalizados (opcional)</p>
                  <p className="text-[11px] text-muted-foreground mb-2">Defina preços só para este cliente. Sem personalizar = usa os preços gerais.</p>
                  <div className="space-y-1.5">
                    {(editing.integracoes || []).map((it, i) => {
                      const iid = it.integration_id || it;
                      const nome = integrations.find(x => x.id === iid)?.name || 'Integração';
                      const tem = !!(editing.precos_personalizados || {})[iid];
                      return (
                        <button key={i} onClick={() => abrirCustomPrecos(it)} className="w-full flex items-center justify-between rounded-lg bg-background/60 border border-border/50 px-3 py-2 hover:border-primary/40">
                          <span className="text-sm text-foreground">{nome}</span>
                          <span className={`text-[11px] ${tem ? 'text-green-500' : 'text-muted-foreground'}`}>{tem ? 'personalizado ✓' : 'definir preços'}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
              {!editing.novo && <div className="flex items-center justify-between rounded-lg bg-muted/40 p-3"><span className="text-sm text-foreground">Revendedor ativo</span><Switch checked={editing.active !== false} onCheckedChange={v => setEditing({ ...editing, active: v })} /></div>}
              <Button onClick={salvarRevendedor} disabled={saving} className="w-full">{saving ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Validando nos painéis...</> : (editing.novo ? 'Criar revendedor' : 'Salvar')}</Button>
            </div>
          </div>
        </div>
      ), document.body)}
    </div>
  );
}
